import React, { useState, useEffect, useContext } from 'react';
import { observer } from 'mobx-react-lite';

import Async from 'react-select/async';

import {
  Paper,
  Grid,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { toast } from 'react-toastify';
import { InputContainer, listRemoveBtnStyle } from './styles';
import { Button } from '../../generators/FormGenerator/styles';

import { ISelectValueType } from '../../store/inputs/types';
import { AssetListInput } from '../../store/inputs/AssetListInput';
import { instance } from '../../services/api';
import { GlobalsContext } from '../../store/Globals';

const CAssetListInput = observer(({ item }: { item: AssetListInput }) => {
  const {
    label,
    value,
    selectorValue,
    onChange,
    onChangeValue,
    disabled,
    dataType,
    isKey,
    isEdit,
  } = item;
  const globals = useContext(GlobalsContext);
  const [readers, setReaders] = useState<string[] | null>(null);
  const [assetListType, setAssetListType] = useState<string>('');
  const [collection, setCollection] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [assets, setAssets] = useState<AssetListInput[]>([]);

  useEffect(() => {
    const assetType = dataType.replace('[]', '');

    const getSchema = async () => {
      try {
        if (
          globals.assetList.some(
            (itm: { tag: string }) => itm.tag === assetType,
          ) &&
          assetType !== '@asset'
        ) {
          const { data } = await instance.post('/api/query/getSchema/', {
            assetType,
          });

          setReaders(data.readers);
          setCollection(data.collection ? data.collection : assetType);
          setAssetListType(assetType);
        }
        if (assetType === '@asset') {
          setAssetListType(assetType);
        }
      } catch (error: any) {
        toast.error(error.message || 'Fail to get asset schema');
      } finally {
        setLoading(false);
      }
    };

    getSchema();
  }, [dataType, globals]);

  useEffect(() => {
    const getAssets = async () => {
      const promises = await Promise.all(
        value
          .filter((v) => v && Object.prototype.hasOwnProperty.call(v, '@key'))
          .filter(
            (v) => v && Object.prototype.hasOwnProperty.call(v, '@assetType'),
          )
          .map((v) =>
            instance.post(`/api/query/readAsset`, {
              key: {
                '@assetType': v['@assetType'].replace('[]', ''),
                '@key': v['@key'],
              },
              resolve: false,
            }),
          ),
      );
      return promises.map((p) => p.data as AssetListInput) as AssetListInput[];
    };

    (async () => {
      if (value) {
        setAssets(await getAssets());
      } else {
        setAssets([]);
      }
    })();
  }, [value, dataType]);

  // Asset options
  const loadOptions = () => {
    return new Promise((resolve) => {
      (async () => {
        try {
          let queryReq;
          if (assetListType === '@asset') {
            queryReq = {
              query: { selector: { '@assetType': { $gt: 0 } }, limit: 100 },
              resolve: false,
            };
          } else {
            queryReq = {
              query: { selector: { '@assetType': assetListType }, limit: 100 },
              ...(readers && { collection }),
              resolve: false,
            };
          }

          const result = await instance.post(`/api/query/search`, queryReq);
          const { data } = result;
          resolve(
            data.result
              ? data.result.map((itm: any) => ({
                  value: itm,
                  label: globals.itemLabel(itm),
                }))
              : [],
          );
        } catch (error: any) {
          if (
            !(
              error &&
              error.response &&
              typeof error.response.data === 'string' &&
              error.response.data.indexOf('access permission') >= 0
            )
          ) {
            toast.error(error.message || 'Fail to search asset');
          }

          resolve([]);
        }
      })();
    });
  };

  // add a new asset
  const addAsset = () => {
    if (selectorValue && typeof selectorValue === 'object') {
      const asset = (selectorValue as ISelectValueType).value;

      if (
        !assets.find(
          (ast) =>
            Object.prototype.hasOwnProperty.call(asset, '@key') &&
            Object.prototype.hasOwnProperty.call(ast, '@key') &&
            (asset as any)['@key'] === (ast as any)['@key'],
        )
      ) {
        const newAssets = [...assets, asset as any];
        onChangeValue(newAssets as any);
        onChange(null); // Clear the asset selector
      }
    }
  };

  // remove an asset
  const removeAsset = (asset: Record<string, any>) => {
    const removeIndex = assets.findIndex(
      (ast) =>
        Object.prototype.hasOwnProperty.call(asset, '@key') &&
        Object.prototype.hasOwnProperty.call(ast, '@key') &&
        asset['@key'] === (ast as any)['@key'],
    );

    if (removeIndex >= 0) {
      const newAssets = [...assets];
      newAssets.splice(removeIndex, 1);
      onChangeValue(newAssets);
    }
  };

  if (loading) return <CircularProgress />;
  return (
    <Grid container wrap="wrap" spacing={3}>
      <Grid item xs={11} style={{ margin: '0 auto' }}>
        <Paper style={{ padding: '5px', margin: '10px 0' }}>
          <InputContainer>
            <Typography style={{ marginBottom: '5px', color: '#777' }}>
              {label}
            </Typography>
            <Async
              isClearable
              cacheOptions
              defaultOptions
              value={selectorValue as any}
              loadOptions={loadOptions}
              placeholder={`Select the ${label}`}
              isDisabled={disabled || (isKey && isEdit)}
              onChange={(v: any) => (v ? onChange(v) : onChange(null))}
            />
            {assets && assets.length
              ? assets.map((asset) => {
                  return (
                    <Button
                      key={(asset as Record<string, any>)['@key']}
                      style={listRemoveBtnStyle}
                      onClick={() => removeAsset(asset)}
                    >
                      <i className="bx bx-x-circle" style={{ color: '#aa0000', fontSize: '24px' }}></i>
                      {globals.itemLabel(asset)}
                    </Button>
                  );
                })
              : null}
          </InputContainer>
          <Button 
            style={{ margin: '0 0 10px 5px' }} 
            onClick={() => addAsset()}
            disabled={disabled || (isKey && isEdit)}
          >
            Add
          </Button>
        </Paper>
      </Grid>
    </Grid>
  );
});

export default CAssetListInput;
